// The season banner (mockup round eleven): the title card that names the
// world, the season and the time left.
//
// The World theme picks one of six looks for it (themes.md §3.4, R825).
// The AUTO look is card 04 "Abyss Title Card"; `sonar-type`, `depth-gauge`,
// `horizon-split`, `echo-count` and `season-bar` are cards 01, 02, 08, 09
// and 10 of the same round. A style changes the markup this module writes
// and the class on `#ui` the stylesheet selects on. It changes NO clock:
// every style arrives, holds and leaves on the one life below.
//
// It goes up on the same frame the chrome dock-in starts (`ui/dockin.ts`).
// The map has spawned by then, the panels are still outside the viewport,
// and for four and a half seconds the glass says which world this is, which
// season it is, and how long is left of it. Then it leaves and the map has
// the screen.
//
// It plays on the two arrivals `render/spawn.ts` names: `login` and
// `season`. A `resync` raises nothing — the player is already looking at
// that world, and a title card there reads as a fault.
//
// **The motion is CSS.** This module raises the element, writes the text
// once, adds one class and takes the element away when the animation ends.
// Every distance, every curve and every phase edge is in `ui/styles.css`,
// which is where a designer can change them. The one thing both sides
// state is the length of the life, and `banner.test.ts` fails when the two
// disagree.
//
// Three rules the mockup's notes make non-negotiable:
//
//  - **It must skip.** The first pointerdown or keydown takes it away, the
//    same as the spawn and the dock-in. A player who reloads four times in
//    a debugging session must not watch this four times.
//  - **It never takes a pointer.** It sits over a black-to-map transition
//    that already needed a watchdog, so `pointer-events: none` holds for
//    its whole life and there is a timeout behind `animationend`: an event
//    that never fires must not leave a card on the glass forever.
//  - **The countdown is world time on the world clock** (R93). The figure
//    comes in as `leftSec` and goes through `seasonCountdown`, which is the
//    top bar's own formatter. The banner computes no second rule for it.

import { hiddenDocument, reducedMotion, skipOnInput, stopSkipOnInput } from '../render/motion';
import type { SpawnKind } from '../render/spawn-plan';
import { esc } from './esc';
import { seasonCountdown } from './format';
import { SEC_PER_DAY } from '../locale/clock';
// The banner's RESTING FORM (R548): the chart furniture's cartouche
// (card 06) stands in the same corner and names the same world and the
// same season, quietly. The two must never share the glass, so the
// banner tells the piece on both edges of a card's life. The call is a
// module function and not a hook the caller passes, because `main.ts`
// builds the banner and the chart registry builds the piece: neither of
// the two can hand the other a function without a third owner.
import { setBannerShowing } from './chart/cartouche';
import { banner as S } from '../gen/strings';

// The card's phases ride data/presentation/presentation.toml
// [presentation.banner] (card 04's own timing), generated into
// gen/presentation.ts and re-exported so the keyframe test keeps its
// one source.
import {
  BANNER_GAUGE_TICKS, BANNER_HOLD_MS, BANNER_IN_MS, BANNER_OUT_MS, BANNER_SLACK_MS,
} from '../gen/presentation';
import { AUTO_CHROME_LOOK } from './chromelook';
import type { BannerStyle } from './settings';

export { BANNER_HOLD_MS, BANNER_IN_MS, BANNER_OUT_MS, BANNER_SLACK_MS };
/** The whole life. One CSS animation covers it end to end. */
export const BANNER_MS = BANNER_IN_MS + BANNER_HOLD_MS + BANNER_OUT_MS;

/**
 * A phase edge as the stylesheet writes it: the fraction of the whole life,
 * in percent, with no trailing zeroes. The keyframes state their stops in
 * these terms and the test reads them back, so moving `BANNER_IN_MS` here
 * without moving the keyframe there is a failure and not a drift.
 */
export function bannerStop(ms: number): string {
  return `${Number(((ms / BANNER_MS) * 100).toFixed(3))}%`;
}

/** What one card says. A plain argument object: the banner reads no global
 *  state, and every string on it is escaped before it reaches the glass. */
export interface BannerCard {
  /** Which arrival this is. Only `login` and `season` raise anything. */
  kind: SpawnKind;
  worldName: string;
  /** The matchmaking region, when the world has one; blank is normal. */
  region?: string;
  season: number;
  /** World-clock seconds left in the season — `state.seasonLeftSec()`, the
   *  same figure the top bar is given. */
  leftSec: number;
  /** How long the whole season is, in world seconds — `state.rules.seasonSec`
   *  (R825). Only the `season-bar` style reads it, and a card that carries
   *  no figure draws the track alone. */
  seasonSec?: number;
}

/** The kicker of every style: the world, and the region where there is one. */
function kickerOf(card: BannerCard): string {
  return [card.worldName, card.region]
    .map((s) => (s ?? '').trim())
    .filter((s) => s !== '')
    .map(esc)
    .join(' · ');
}

/** The season as a card may print it. A truncated hello leaves 0 or NaN on
 *  the wire field, and "Season NaN" is worse than a card that reads 1. */
function seasonOf(card: BannerCard): number {
  return Number.isFinite(card.season) ? Math.max(1, Math.floor(card.season)) : 1;
}

/**
 * What the `season-bar` style draws: the fraction of the season behind the
 * player, the day the fill reaches, and how many days the season holds.
 *
 * The two figures come from the same clock the countdown runs on (§8, R93):
 * `seasonSec` is the hello's own length of a season and `leftSec` is
 * `state.seasonLeftSec()`. A season the client cannot measure — a truncated
 * hello, a world with no season length — gives `days` 0, and the card draws
 * the track with no fill and no labels.
 */
export function seasonBarFigures(card: BannerCard): { frac: number; day: number; days: number } {
  const season = Number.isFinite(card.seasonSec) ? (card.seasonSec as number) : 0;
  if (!(season > 0)) return { frac: 0, day: 0, days: 0 };
  const left = Math.min(Math.max(0, Number.isFinite(card.leftSec) ? card.leftSec : 0), season);
  const elapsed = season - left;
  return {
    frac: Number((elapsed / season).toFixed(3)),
    day: Math.floor(elapsed / SEC_PER_DAY) + 1,
    days: Math.round(season / SEC_PER_DAY),
  };
}

/** The three parts every style writes, and the card they came from. */
interface BannerParts {
  card: BannerCard;
  /** `.kick`: the world and its region. */
  kick: string;
  /** The season sentence, which most styles wrap as `.hero`. */
  hero: string;
  /** The countdown figure, escaped. */
  left: string;
  /** `.foot`: the countdown sentence. */
  foot: string;
}

/**
 * The markup of each banner style (R825, ui-map-interface.md §3.3). Every
 * style keeps the same three parts — `.kick` the world and its region,
 * `.hero` the season, `.foot` the countdown — so a reader takes the same
 * three facts off every card. What a style adds beside them is its own,
 * and the motion of each is in `ui/styles.css` under
 * `#ui.theme-banner-<id>`.
 */
const STYLE_MARKUP: Record<BannerStyle, (p: BannerParts) => string> = {
  // Card 04 "Abyss Title Card", the AUTO look: a film title. The kicker is
  // the world and its region, the hero is the season, the rule is a
  // hairline that draws itself out from the centre, and the footer is the
  // countdown.
  abyss: ({ kick, hero, foot }) =>
    kick + `<div class="hero">${hero}</div><div class="rule"></div>` + foot,

  // Card 01 "Sonar Type": one hero, twice. The dim copy is the type the
  // instrument has not reached yet and the lit copy is the type behind the
  // sweep, so the mask over the lit one is the whole of the look.
  'sonar-type': ({ kick, hero, foot }) => kick
    + `<div class="hero"><span class="base">${hero}</span>`
    + `<span class="lit" aria-hidden="true">${hero}</span></div>`
    + '<div class="rule"></div>'
    + foot,

  // Card 02 "Depth Gauge": an instrument and not a title. A tick rule over
  // a ruled frame, the season as a figure that rolls into place, and the
  // countdown as the reading under it.
  'depth-gauge': ({ card, kick, left }) => {
    let ticks = '';
    for (let i = 0; i < BANNER_GAUGE_TICKS; i++) {
      ticks += `<span class="t${i % 5 === 0 ? ' tall' : ''}"></span>`;
    }
    return '<div class="frame">'
      + `<div class="ticks">${ticks}</div>`
      + kick
      + `<div class="gauge"><span class="word">${S.gauge_season}</span>`
      + `<span class="roll">${seasonOf(card)}</span></div>`
      + `<div class="foot"><span class="time">${left}</span>`
      + `<small>${S.remaining}</small></div>`
      + '</div>';
  },

  // Card 08 "Horizon Split": two letterbox bands close over the map and the
  // words are revealed between them. The bands are absolute and the words
  // ride their own box, because the reveal clips that box alone.
  'horizon-split': ({ kick, hero, foot }) =>
    '<i class="band top"></i><i class="band bot"></i>'
    + `<div class="mid">${kick}<div class="hero">${hero}</div>${foot}</div>`,

  // Card 09 "Echo Count": the season stamps, and two fainter copies expand
  // out of it and die. The two echoes say nothing a reader must read, so a
  // screen reader is given the stamp alone.
  'echo-count': ({ kick, hero, foot }) => kick
    + '<div class="echoes">'
    + `<span class="hero e2" aria-hidden="true">${hero}</span>`
    + `<span class="hero e1" aria-hidden="true">${hero}</span>`
    + `<span class="hero e0">${hero}</span>`
    + '</div>'
    + foot,

  // Card 10 "Season Bar": the countdown as a picture. The fill reaches the
  // day the season has reached, and the two labels name the ends.
  'season-bar': ({ card, kick, hero, foot }) => {
    const f = seasonBarFigures(card);
    const bar = f.days > 0
      ? `<div class="bar" style="--season-frac:${f.frac}">`
        + '<div class="track"></div><div class="fill"></div><div class="head"></div>'
        // The mark stands OVER the head and not in the label row: at the
        // start of a season the fill is short, and the mark would print on
        // top of the `day 1` label there.
        + `<span class="mark">${S.bar_day({ day: f.day })}</span>`
        + `<div class="days"><span>${S.bar_day({ day: 1 })}</span>`
        + `<span>${S.bar_day({ day: f.days })}</span></div></div>`
      : '<div class="bar"><div class="track"></div></div>';
    return kick + `<div class="hero">${hero}</div>` + bar + foot;
  },
};

/**
 * The card's markup, for one banner style. Exported for the test, and
 * pure: the class only ever assigns what this returns.
 *
 * The case is CSS's business (`text-transform: uppercase`), so a world
 * named "Trench of Ur" keeps its own spelling here. Every string the
 * server sent goes through `esc` first, under every style.
 */
export function bannerMarkup(
  card: BannerCard,
  style: BannerStyle = AUTO_CHROME_LOOK.banner,
): string {
  const left = esc(seasonCountdown(card.leftSec));
  return STYLE_MARKUP[style]({
    card,
    kick: `<div class="kick">${kickerOf(card)}</div>`,
    hero: S.hero_season({ season: seasonOf(card) }),
    left,
    foot: `<div class="foot">${S.foot_remaining({ left })}</div>`,
  });
}

/**
 * The controller main.ts composes: one element, one `raise` per arrival.
 *
 * The classes it sets on that element are its whole interface to the
 * stylesheet:
 *
 * - `on` — the card is up and the 4.5 s animation is running.
 * - `still` — reduced motion: no tracking travel and no stagger, only the
 *   fade in, the hold and the fade out.
 */
export class SeasonBanner {
  private up = false;
  private timer = 0;

  /** Which of the banner looks the card wears (themes.md §3.4). */
  private styleId: BannerStyle;

  constructor(
    private readonly el: HTMLElement,
    style: BannerStyle = AUTO_CHROME_LOOK.banner,
    private readonly reduced: () => boolean = reducedMotion,
  ) {
    this.styleId = style;
  }

  /** Presentation-only; the tests and the browser check read it. */
  get showing(): boolean { return this.up; }

  /** The look in force (R825). The markup and the stylesheet both read it. */
  get style(): BannerStyle { return this.styleId; }

  /**
   * Sets the look. The game writes it one time, at the start of a session,
   * because a theme cannot change during a match (themes.md §2); the theme
   * preview writes it between two cards.
   */
  setStyle(id: BannerStyle): void {
    this.styleId = id;
  }

  /**
   * Raises the card, if this arrival has one to raise. It answers whether
   * it did, because the caller hangs it off two events — the spawn's late
   * point and its end — and only the first of them is the arrival.
   *
   * Three arrivals raise nothing:
   *
   *  - a `resync`, which is not an arrival at all;
   *  - a second call while a card is up, which is the spawn's `onEnd`
   *    arriving behind its own `onLate`;
   *  - a hidden document. A background tab runs no CSS animation, so the
   *    card would stand still until the player came back and then play to
   *    an audience that had already read it. `ChromeDockIn.dock()` refuses
   *    the same way, for the same reason.
   */
  raise(card: BannerCard): boolean {
    if (card.kind === 'resync' || this.up) return false;
    if (hiddenDocument()) return false;
    this.el.innerHTML = bannerMarkup(card, this.styleId);
    this.el.classList.toggle('still', this.reduced());
    // Removed, read, added: a card raised twice inside one frame must start
    // its animation again rather than continue the last one.
    this.el.classList.remove('on');
    void this.el.offsetHeight;
    this.el.classList.add('on');
    this.up = true;
    setBannerShowing(true);

    // The card is over the map at the moment the map is new, so any input
    // at all takes it away (the mockup's own rule). The event is not
    // swallowed: the map is live under the card, and the click that clears
    // it may also select the fleet it landed on.
    skipOnInput(this.skipper);
    this.el.addEventListener('animationend', this.ender);
    // And the belt. A tab that loses its compositor mid-animation, a
    // browser that drops the event, a stylesheet that failed to load: none
    // of them may leave a title card on the glass for the whole session.
    this.timer = window.setTimeout(() => this.clear(), BANNER_MS + BANNER_SLACK_MS);
    return true;
  }

  /** Takes the card away now. Any pointerdown or keydown calls this. */
  skip() { if (this.up) this.clear(); }

  private readonly skipper = () => this.skip();

  /** The end of the animation is the end of the card. Sub-elements animate
   *  too and their events bubble to here, so the name is checked: only the
   *  life of the whole card ends it. */
  private readonly ender = (ev: AnimationEvent) => {
    if (ev.animationName === 'banner-life') this.clear();
  };

  /** For a teardown of the whole client. */
  destroy() { this.clear(); }

  /** The one way down, from every path: the classes off, the text gone, and
   *  no listener and no timer left behind. */
  private clear() {
    this.up = false;
    setBannerShowing(false);
    if (this.timer) { clearTimeout(this.timer); this.timer = 0; }
    // A listener that is not on the target comes off as a no-op.
    this.el.removeEventListener('animationend', this.ender);
    stopSkipOnInput(this.skipper);
    this.el.classList.remove('on', 'still');
    // `display: none` follows the class, but the markup would stay in the
    // document and the hero's tracking animation with it. Nothing is left
    // costing anything one frame after the card ends.
    this.el.innerHTML = '';
  }
}
